import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { map, catchError } from 'rxjs/operators';
import { of as RxOf } from 'rxjs/observable/of';
import { forkJoin as RxForkJoin } from 'rxjs/observable/forkJoin';
import {
  isNil as _isNil, find as _find, isEmpty as _isEmpty, forEach as _forEach, map as _map, cloneDeep as _cloneDeep,
  filter as _filter, some as _some
} from 'lodash';

import { BusinessMenuStatus } from '@blcloud/bl-datamodel/enum/bizcmn/business-menu-status';
import { OfferUserDataDiv } from '@blcloud/bl-datamodel/enum/common/offer-user-data-div';
import { ProductCode } from '@blcloud/bl-datamodel/enum/common/product-code';
import { IsshinTasukeBusinessTabDiv } from '@blcloud/bl-datamodel/enum/bizcmn/isshin-tasuke-business-tab-div';
import { IsshinTasukeBusinessTabDefaultDiv } from '@blcloud/bl-datamodel/enum/bizcmn/isshin-tasuke-business-tab-default-div';
import {
  IIsshinTasukeMenuContent,
  IIsshinTasukeMenuGroup,
  IIsshinTasukeMenuSetting,
  IPermissionCheck,
  IUserInformation
} from '@blcloud/bl-datamodel';
import { IsshinTasukeMenuSettingResource, UserInformationResource } from '@blcloud/bl-ng-resource';
import { Rules } from '@blcloud/bl-ng-ui-component';

import { IDispIsshinTasukeGroupData, IDispIsshinTasukeMenuData } from './isshin-tasuke-menu.interface';
import { BizMenuContentEx, BizMenuGroup, BizMenuTabOption } from '../biz-menu-page.interface';
import { MaintenanceTabId } from '../biz-menu-page.const';

@Injectable()
export class IsshinTasukeMenuService {
  /** 共通メニュー(提供)件数 */
  private readonly OFFER_MENU_COUNT = 9;
  /** 共通メニュー(ユーザー登録)件数 */
  private readonly USER_MENU_COUNT = 2;
  /** 業務メニュー件数 */
  private readonly PRODUCT_MENU_COUNT = 10;
  /** 一新多助メニュー機能のリリース日 */
  private readonly releaseDate = '2026-09-15T00:00:00';

  constructor(
    private isshinTasukeMenuSettingResource: IsshinTasukeMenuSettingResource,
    private userInformationResource: UserInformationResource,
  ) {}

  /**
   * 一新多助メニュー設定の検証ルールを取得します。
   * @returns 検証ルール
   */
  getIsshinTasukeMenuSettingRules(): Observable<Rules[]> {
    return this.isshinTasukeMenuSettingResource.getRules<Rules[]>({ params: { r: '1' } }).pipe(
      catchError(() => {
        return RxOf([]);
      })
    );
  }

  /**
   * 一新多助メニュー設定を取得します。
   * @returns 一新多助メニュー設定
   */
  getIsshinTasukeMenuSetting(blUserId: string): Observable<IIsshinTasukeMenuSetting> {
    return RxForkJoin(
      this.userInformationResource.getById<IUserInformation>(blUserId),
      this.isshinTasukeMenuSettingResource.getById<IIsshinTasukeMenuSetting>(blUserId)
    ).pipe(
      map(([userInfo, setting]) => {
        // BL利用者IDが空の場合は設定未登録
        if (!setting.blUserId) {
          // 一新多助メニュー機能のリリース以降に登録されたアカウントの場合、一新多助メニュータブを表示
          if (userInfo.createDateTime && userInfo.createDateTime >= this.releaseDate) {
            setting.isshinTasukeBusinessTabDiv = IsshinTasukeBusinessTabDiv.All;
            setting.isshinTasukeBusinessTabDefaultDiv = IsshinTasukeBusinessTabDefaultDiv.IsshinTasuke;
          } else {
            setting.isshinTasukeBusinessTabDiv = IsshinTasukeBusinessTabDiv.Hide;
            setting.isshinTasukeBusinessTabDefaultDiv = IsshinTasukeBusinessTabDefaultDiv.IsshinTasuke;
          }
        }

        return setting;
      }),
      catchError(() => {
        // 業務を続行できるようにスルー
        return RxOf(<IIsshinTasukeMenuSetting>{
          isshinTasukeBusinessTabDiv: IsshinTasukeBusinessTabDiv.Hide,
          isshinTasukeBusinessTabDefaultDiv: IsshinTasukeBusinessTabDefaultDiv.IsshinTasuke
        });
      }),
      map(response => {
        // 共通メニューは提供9件・ユーザー登録2件まで空データを追加
        response.isshinTasukeCommonMenuGroup = this.complementCommonMenuGroup(
          response.isshinTasukeCommonMenuGroup, this.OFFER_MENU_COUNT, this.USER_MENU_COUNT
        );

        // 業務メニューは10件まで空データを追加
        response.isshinTasukeMaintenanceMenuGroup = this.complementGroup(response.isshinTasukeMaintenanceMenuGroup, this.PRODUCT_MENU_COUNT);
        response.isshinTasukeRepairMenuGroup = this.complementGroup(response.isshinTasukeRepairMenuGroup, this.PRODUCT_MENU_COUNT);
        response.isshinTasukeCarsalesMenuGroup = this.complementGroup(response.isshinTasukeCarsalesMenuGroup, this.PRODUCT_MENU_COUNT);

        return response;
      })
    );
  }

  /**
   * 業務グループリストを取得します。
   * @param tabOptions タブオプションリスト
   * @returns 業務グループリスト
   */
  getBusinessGroupList(tabOptions: BizMenuTabOption[]): IIsshinTasukeMenuGroup[] {
    return _map(tabOptions, tab => {
      return {
        businessMenuGroupId: tab.menuGroup.businessMenuGroupId,
        businessMenuGroupName: tab.menuGroup.businessMenuGroupName,
      }
    });
  }

  /**
   * 全業務メニューリストを取得します。
   * @param bizMenuGroup 業務メニューグループ
   * @param tabOptions タブオプションリスト
   * @returns 全業務メニューリスト
   */
  getBusinessAllMenuList(bizMenuGroup: BizMenuGroup, tabOptions: BizMenuTabOption[]): IIsshinTasukeMenuContent[] {
    const list: IIsshinTasukeMenuContent[] = []
    _forEach(tabOptions, tab => {
      const menuItems = bizMenuGroup[tab.tabId];

      if (!_isEmpty(menuItems)) {
        _forEach(menuItems, item => {
          _forEach(item.menus, menu => {
            if (menu.businessMenuContentId && menu.businessMenuContentName && menu.businessMenuStatus === BusinessMenuStatus.Available) {
              list.push({
                businessMenuGroupId: tab.menuGroup.businessMenuGroupId,
                businessMenuGroupName: tab.menuGroup.businessMenuGroupName,
                businessMenuContentId: menu.businessMenuContentId,
                businessMenuContentName: menu.businessMenuContentName,
              });
            }
          });
        });
      }
    });

    return list;
  }

  /**
   * 一新多助メニューデータを作成します。
   * @param setting 一新多助メニュー
   * @param bizMenuGroup 業務メニューリスト
   * @param isPermissionMaintenance 整備サービス権限フラグ
   * @param isPermissionCarsales 鈑金サービス権限フラグ
   * @param isPermissionRepair 車販サービス権限フラグ
   * @param availableServiceList 有効なサービスIDリスト
   * @param permissionList 権限なしリスト
   * @returns 一新多助メニューデータ
   */
  createIsshinTasukeMenuData(
    setting: IIsshinTasukeMenuSetting,
    bizMenuGroup: BizMenuGroup,
    isPermissionMaintenance: boolean,
    isPermissionCarsales: boolean,
    isPermissionRepair: boolean,
    availableServiceList: string[],
    permissionList: IPermissionCheck[],
  ): IDispIsshinTasukeMenuData {
    const productMenuGroups: IDispIsshinTasukeGroupData[] = [];
    const data: IDispIsshinTasukeMenuData = {
      offerCommonMenuGroup: { businessMenuGroupId: 0, bizMenuContents: [] },
      userCommonMenuGroup: { businessMenuGroupId: 0, bizMenuContents: [] },
      productMenuGroups: productMenuGroups,
      isshinTasukeMenuSetting: setting,
    }

    // 共通メニュー(提供・ユーザー登録)
    const commonGroup = setting.isshinTasukeCommonMenuGroup;
    if (commonGroup && !_isEmpty(commonGroup.isshinTasukeMenuContentList)) {
      const offerContentList = _filter(commonGroup.isshinTasukeMenuContentList, c => c.offerUserDataDiv === OfferUserDataDiv.Offer);
      const userContentList = _filter(commonGroup.isshinTasukeMenuContentList, c => c.offerUserDataDiv !== OfferUserDataDiv.Offer);

      data.offerCommonMenuGroup = this.createGroupData(
        { ...commonGroup, isshinTasukeMenuContentList: offerContentList }, bizMenuGroup, availableServiceList, permissionList, true
      );
      data.userCommonMenuGroup = this.createGroupData(
        { ...commonGroup, isshinTasukeMenuContentList: userContentList }, bizMenuGroup, availableServiceList, permissionList, false
      );
    }

    // 業務メニュー(整備・車販・鈑金)
    const productGroupDefs = [
      { tabId: MaintenanceTabId.SuperFrontman, group: setting.isshinTasukeMaintenanceMenuGroup, permission: isPermissionMaintenance, productCode: ProductCode.SuperFrontman },
      { tabId: MaintenanceTabId.CarSales, group: setting.isshinTasukeCarsalesMenuGroup, permission: isPermissionCarsales, productCode: ProductCode.CarSales },
      { tabId: MaintenanceTabId.Repair, group: setting.isshinTasukeRepairMenuGroup, permission: isPermissionRepair, productCode: ProductCode.Repair },
    ];
    _forEach(productGroupDefs, ({ tabId, group, permission, productCode }) => {
      if (bizMenuGroup[tabId] && group && permission) {
        productMenuGroups.push(this.createGroupData(group, bizMenuGroup, availableServiceList, permissionList, false, productCode));
      }
    });

    // 最大3件まで空グループを追加
    while (productMenuGroups.length < 3) {
      productMenuGroups.push({ businessMenuGroupId: 0 });
    }

    return data;
  }

  /**
   * メニューグループの表示データを作成します。
   * @param group 一新多助メニューグループ
   * @param bizMenuGroup 業務メニューリスト
   * @param availableServiceList 有効なサービスIDリスト
   * @param permissionList 権限なしリスト
   * @param isOfferCommonGroup 提供共通メニューグループかどうか
   * @param productCode プロダクトコード（整備・車販・鈑金の業務メニューグループの場合のみ指定）
   * @returns 一新多助メニューグループの表示データ
   */
  private createGroupData(
    group: IIsshinTasukeMenuGroup,
    bizMenuGroup: BizMenuGroup,
    availableServiceList: string[],
    permissionList: IPermissionCheck[],
    isOfferCommonGroup: boolean,
    productCode?: string
  ): IDispIsshinTasukeGroupData {
    const contentList = group.isshinTasukeMenuContentList || [];
    const usedNames = new Set<string>();
    const mergeMenuList: BizMenuContentEx[] = [];

    _forEach(contentList, c => {
      // サービスIDが有効なサービスに含まれない場合、またはBL機能IDが権限なし機能に含まれる場合は非表示
      const isServiceUnavailable = !!c.serviceId && !availableServiceList.includes(c.serviceId);
      const isPermissionDenied = !_isEmpty(c.blFunctionIdList)
        && _some(c.blFunctionIdList, blFunctionId => _some(permissionList, p => p.blFunctionId === blFunctionId));

      if (isServiceUnavailable || isPermissionDenied) {
        if (isOfferCommonGroup) {
          return;
        }
        mergeMenuList.push(this.mergeBizMenuContent({}, bizMenuGroup, productCode));
        return;
      }

      const mergedContent = this.mergeBizMenuContent(c, bizMenuGroup, productCode);

      if (c.offerUserDataDiv === OfferUserDataDiv.Offer) {
        // ID指定だが該当する業務メニューが存在しない場合は配列から除外
        if (c.businessMenuGroupId && c.businessMenuContentId && !mergedContent.businessMenuContentId) {
          return;
        }

        // 名称が重複するメニューは2件目以降を配列から除外
        if (mergedContent.businessMenuContentName) {
          if (usedNames.has(mergedContent.businessMenuContentName)) {
            return;
          }
          usedNames.add(mergedContent.businessMenuContentName);
        }
      }

      mergeMenuList.push(mergedContent);
    });

    return {
      businessMenuGroupId: group.businessMenuGroupId || undefined,
      businessMenuGroupName: group.businessMenuGroupName || undefined,
      businessMenuIconCss: group.businessMenuIconCss || undefined,
      bizMenuContents: mergeMenuList
    };
  }

  /**
   * 業務メニューコンテンツをマージします。
   * @param itMenuContent 一新多助メニューコンテンツ
   * @param bizMenus 業務メニューリスト
   * @param productCode プロダクトコード（整備・車販・鈑金の業務メニューグループの場合のみ指定）
   * @returns マージ後メニューコンテンツ
   */
  private mergeBizMenuContent(itMenuContent: IIsshinTasukeMenuContent, bizMenuGroup: BizMenuGroup, productCode?: string): BizMenuContentEx {
    if (!_isNil(itMenuContent)) {
      // グループID、コンテンツIDが指定されている場合、業務メニューをマージ
      if (itMenuContent.businessMenuGroupId && itMenuContent.businessMenuContentId) {
        const findGroupItems = bizMenuGroup[`${itMenuContent.businessMenuGroupId}`];

        if (!_isEmpty(findGroupItems)) {
          for (let index = 0; index < findGroupItems.length; index++) {
            const menuItem = findGroupItems[index];
            const findContent = _find(menuItem.menus, c => c.businessMenuContentId === itMenuContent.businessMenuContentId);

            if (findContent && findContent.businessMenuStatus === BusinessMenuStatus.Available) {
              // 業務メニューデータをベースに一新多助メニューデータをマージ
              const result = _cloneDeep(findContent);
              if (itMenuContent.businessMenuContentName) {
                result.businessMenuContentName = itMenuContent.businessMenuContentName;
              }
              if (itMenuContent.businessMenuIconCss) {
                result.businessMenuIconCss = itMenuContent.businessMenuIconCss;
              }
              if (!_isEmpty(itMenuContent.urlList)) {
                result.urlList = itMenuContent.urlList;
              }
              if (productCode) {
                result.productCode = productCode;
              }
              return result;
            }
          }
        }
      } else if (itMenuContent.businessMenuContentName && !_isEmpty(itMenuContent.urlList)) {
        return {
          lineBottom: false,
          businessMenuContentName: itMenuContent.businessMenuContentName,
          businessMenuIconCss: itMenuContent.businessMenuIconCss,
          urlList: itMenuContent.urlList,
          productCode
        };
      }
    }

    // 空メニュー
    return {
      lineBottom: false,
      businessMenuContentId: 0,
      businessMenuContentName: '',
      businessMenuIconCss: '',
      urlList: []
    };
  }

  /**
   * メニューグループのデータを補完します。
   * @param group コンテンツリスト
   * @param menuCount メニュー件数
   * @returns 補完後のメニューグループ
   */
  private complementGroup(group: IIsshinTasukeMenuGroup | null | undefined, menuCount: number): IIsshinTasukeMenuGroup {
    const result: IIsshinTasukeMenuGroup = group || {
      businessMenuGroupId: 0,
      businessMenuGroupName: '',
      businessMenuIconCss: '',
      isshinTasukeMenuContentList: []
    };

    result.isshinTasukeMenuContentList = result.isshinTasukeMenuContentList || [];

    for (let index = result.isshinTasukeMenuContentList.length; index < menuCount; index++) {
      result.isshinTasukeMenuContentList.push({
        businessMenuGroupId: 0,
        businessMenuContentId: 0,
        businessMenuContentName: ''
      });
    }

    return result;
  }

  /**
   * 共通メニューグループのデータを提供・ユーザー登録それぞれの件数まで補完します。
   * @param group コンテンツリスト
   * @param offerMenuCount 提供メニュー件数
   * @param userMenuCount ユーザー登録メニュー件数
   * @returns 補完後のメニューグループ
   */
  private complementCommonMenuGroup(
    group: IIsshinTasukeMenuGroup | null | undefined,
    offerMenuCount: number,
    userMenuCount: number
  ): IIsshinTasukeMenuGroup {
    const result: IIsshinTasukeMenuGroup = group || {
      businessMenuGroupId: 0,
      businessMenuGroupName: '',
      businessMenuIconCss: '',
      isshinTasukeMenuContentList: []
    };

    const contentList = result.isshinTasukeMenuContentList || [];
    const offerContentList = _filter(contentList, c => c.offerUserDataDiv === OfferUserDataDiv.Offer);
    const userContentList = _filter(contentList, c => c.offerUserDataDiv !== OfferUserDataDiv.Offer);

    for (let index = offerContentList.length; index < offerMenuCount; index++) {
      offerContentList.push({
        businessMenuGroupId: 0,
        businessMenuContentId: 0,
        businessMenuContentName: '',
        offerUserDataDiv: OfferUserDataDiv.Offer
      });
    }

    for (let index = userContentList.length; index < userMenuCount; index++) {
      userContentList.push({
        businessMenuGroupId: 0,
        businessMenuContentId: 0,
        businessMenuContentName: '',
        offerUserDataDiv: OfferUserDataDiv.User
      });
    }

    result.isshinTasukeMenuContentList = [...offerContentList, ...userContentList];

    return result;
  }
}



// WEBPACK FOOTER //
// ./src/app/page/biz-menu-page/isshin-tasuke-menu/isshin-tasuke-menu.service.ts